import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useAtomValue } from "jotai";
import { formSessionAtom } from "@/state/formSession";
import { QUERY_KEYS } from "@/constants/queryKeys";
import type { DriverCalculations, DriverName } from "@/types/Calculation";
import { isApiErrorType } from "@/utils/errors";
import { CalculationsApi } from "@/api/CalculationsApi";
import { CalculationCard } from "@/components/Cards";
import SectionFetchError from "@/components/SectionFetchError";

const POLL_MS = 15_000;

const drivers: DriverName[] = ["optimal", "autoconsumption", "fixed_hours"];

const getStatusCode = (err: AxiosError): number | undefined => err.response?.status;

export const Step6: FC = () => {
  const { t } = useTranslation();
  const { formId } = useAtomValue(formSessionAtom);

  const { data, isPending, isError, error, refetch } = useQuery<DriverCalculations, AxiosError>({
    queryKey: [QUERY_KEYS.CALCULATION_RESULT, { formId }],
    queryFn: () => CalculationsApi.getFreeResults(formId ?? ""),
    retry: (failureCount, err) => {
      const status = getStatusCode(err);
      if (status === 404) return true;
      return failureCount < 1;
    },
    enabled: Boolean(formId),
    retryDelay: (_, err) => (getStatusCode(err) === 404 ? POLL_MS : 2000),
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: true,
  });

  const waiting404 = isError && getStatusCode(error) === 404;
  const showError = isError && !waiting404;
  const isCalculationError =
    isApiErrorType(error, "calculation_failed") || isApiErrorType(error, "no_savings_file");

  const bestDriver = useMemo(() => {
    if (!data) return null;
    return drivers.reduce((best, current) =>
      data[current].total_savings > data[best].total_savings ? current : best,
    );
  }, [data]);

  const handleRefetch = () => {
    if (isCalculationError) {
      window.sessionStorage.clear();
      const cleanUrl = `${window.location.pathname}${window.location.hash}`;
      window.location.assign(cleanUrl);
    } else void refetch();
  };

  if (showError)
    return (
      <SectionFetchError
        refetch={handleRefetch}
        message={isCalculationError ? t("step6.calculationErrorMessage") : undefined}
      />
    );

  const isLoadingLike = isPending || waiting404;

  return (
    <div className="flex flex-col gap-4 lg:gap-10">
      {isLoadingLike && (
        <p className="text-sm" data-test="step6-loading-hint">
          {t("step6.loadingHint")}
        </p>
      )}
      {drivers.map(driver => (
        <CalculationCard
          key={driver}
          driverName={driver}
          isLoading={isLoadingLike}
          data={data?.[driver]}
          highlight={bestDriver === driver}
        />
      ))}
    </div>
  );
};
